import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { firstValueFrom, Observable } from 'rxjs';
import { getEnvironment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ConnectionService } from './connection.service';
import { ChatFeedService } from './chat-feed.service';

@Injectable({
    providedIn: 'root',
})
export class RagService {
    /**
     * Keycloak
     */
    private readonly keycloak = inject(Keycloak);

    constructor(private connection: ConnectionService, private http: HttpClient, private chatFeedService: ChatFeedService) { }

    /**
     * Send a prompt for a collection or a base chat to the LLM. Await partial responses and emit them via an observable.
     * @param prompt Promt to send
     * @param collectionId (optional) Collection id
     * @param chatId (optional) Chat id, used to continue converstation.
     * @param attachmentIds (optional) list of attachment_ids to add to the prompt in the backend. Should refer to DB table message_attachments.id
     * @param retrieveChunks (optional) Search through document chunks for response. Default is false
     * @param generateTitle (optional) true if the backend should generate a title for this chat. Default is true
     * @returns Promise with an observable emitting ChatResponse objects
     */
    async chat(
        prompt: string,
        collectionId?: string,
        chatId?: string,
        attachmentIds?: string[],
        retrieveChunks = false,
        generateTitle = true,
    ): Promise<Observable<ChatResponse>> {
        let response: Response;
        // Collection has been defined
        if (collectionId) {
            response = await fetch(
                `${getEnvironment().apiRootURL}/rag/collections/${collectionId}/chats${chatId ? `/${chatId}/continue` : ''}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.keycloak.token}`,
                        ...(this.connection.role.value && { 'x-hasura-role': this.connection.role.value }),
                    },
                    body: JSON.stringify({
                        prompt, stream: true, retrieve_chunks: retrieveChunks, generate_title: generateTitle,
                    }),
                },
            );
        } else {
            // Base model chat
            response = await fetch(
                `${getEnvironment().apiRootURL}/base/chats${chatId ? `/${chatId}/continue` : ''}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.keycloak.token}`,
                        ...(this.connection.role.value && { 'x-hasura-role': this.connection.role.value }),
                    },
                    body: JSON.stringify({
                        prompt, stream: true, generate_title: generateTitle, attachment_ids: attachmentIds,
                    }),
                },
            );
        }
        if (response.status !== 200) {
            throw new Error(`Rag backend returned status ${response.status}`);
        }
        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('ReadableStream not supported or unavailable.');
        }

        return new Observable<ChatResponse>((subscriber) => {
            const decoder = new TextDecoder();

            // eslint-disable-next-line func-style
            const read = (unprocessedChunkContent: string = ''): void => {
                reader.read().then(({ done, value }) => {
                    // Complete after reading all chunks
                    if (done) {
                        subscriber.complete();
                        // Notify subscribes that chat has been created
                        this.chatFeedService.chatFeedChanged.next();
                        return;
                    }

                    // Handle new chunk content. In theory the content could be partial, one or multiple chunks worth of data.
                    unprocessedChunkContent += decoder.decode(value, { stream: true });
                    try {
                        // Base case: Expect the chunk to exactly contain a JSON object
                        subscriber.next(JSON.parse(unprocessedChunkContent) as ChatResponse);
                        unprocessedChunkContent = '';
                    } catch {
                        // If JSON parse failed the reader found a partial or combined chunk
                        // Read over the unprocessed content and attempt to find one or multiple JSON objects
                        let partial = '';
                        for (const char of unprocessedChunkContent) {
                            partial += char;
                            // Likely end of a JSON object, avoids unnecessary operations
                            if (char === '}') {
                                try {
                                    subscriber.next(JSON.parse(partial) as ChatResponse);
                                    // Reset for the next JSON chunk
                                    partial = '';
                                } catch {
                                    // Continue accumulating if parsing fails
                                }
                            }
                        }
                        // Set unprocessedChunkContent to what was not yet parsed to JSON
                        unprocessedChunkContent = partial;
                    }
                    read(unprocessedChunkContent); // Continue reading the stream - pass the unprocessed content to the next call
                }).catch((err: unknown) => subscriber.error(err));
            };
            read(); // Start reading the stream
            return () => reader.cancel(); // Cleanup on unsubscribe
        });
    }

    /**
     * Send a request to the backend to process an uploaded file as attachment
     * This will parse the full file and store it in DB table message_attachments
     * @param fileId ID of the file to parse. Must be in files table and Minio
     * @returns response with generated message_attachments.id and file_id - or throws error if something went wrong
     */
    attachmentProcess(fileId: string): Promise<AttachmentProcessResponse> {
        return firstValueFrom(this.http.post<AttachmentProcessResponse>(
            `${getEnvironment().apiRootURL}/base/attachmentprocess`,
            {
                fileId: fileId,
            },
            { headers: { 'Authorization': `Bearer ${this.keycloak.token}`, 'x-hasura-role': this.connection.role.value! } },
        ));
    }
}

export interface AttachmentProcessResponse {
    id: string;
    file_id: string;
}

export interface ChatResponse {
    type: 'contentchunk' | 'chatendsources' | 'reasoningchunk' | 'error';
    reasoning_content?: string;
    content?: string;
    id?: string;
    role?: 'assistant';
    created_at?: string;
    sources?: ChatSource[];
}

export interface ChatSource {
    id: string;
    text: string;
    pages?: number[];
    cosine_similarity: number;
    reranker_score: number;
    document: {
        id: string;
        name: string;
        file_id: string;
        deleted_at: string | null;
    };
}
